import { Box, VStack } from '@chakra-ui/layout';
import { Spacer } from '@chakra-ui/react';
import { TAG_VARIANTS } from 'library/atoms/Tag/Tag';
import TagStack from 'library/atoms/Tag/TagStack';
import { PRIORITY_OPTIONS } from 'library/common/constant';
import { FormSelect } from 'library/molecules';
import FormControlWrapper from 'library/molecules/FormControlWrapper';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { uniqueArrayObjects } from 'views/main/organization/analytics-v2/Filters/helper';
import {
  IncidentAnalyticsFilterResponseAssignee,
  IncidentAnalyticsFilterResponseAssigneeType,
} from 'views/main/organization/analytics-v3/api/types';
import {
  AssigneeDropdownOptions,
  Assignee,
  OnCallUsersAnalyticsFilterForm,
} from 'views/main/organization/analytics-v3/hooks/types';
import { SquadFilterDropdown } from 'views/main/organization/owner-filters/Dropdown/AssigneeDropdown';
import { FilterFormProps } from './type';
import { DropdownOption } from 'views/main/organization/owner-filters/hooks/useEntityOwnerFilter';

const getDefaultAssignee = (): Assignee => ({
  meAndMySquads: false,
  users: [],
  squads: [],
  myUsers: [],
  mySquads: [],
});

const handleAssigneeChange = (
  value: any,
  currentAssignee: Assignee,
  setValue: (name: keyof OnCallUsersAnalyticsFilterForm, value: any) => void,
  fieldName: 'schedule' | 'scheduleOwner',
  meAndMySquads: IncidentAnalyticsFilterResponseAssignee['youAndYourSquads'],
) => {
  if (value?.type === 'select-all') {
    setValue(fieldName, {
      meAndMySquads: true,
      myUsers: meAndMySquads.map(el => ({
        ...el,
        assigneeType: IncidentAnalyticsFilterResponseAssigneeType.User,
        value: el.id,
      })),
      mySquads: meAndMySquads.map(el => ({
        ...el,
        assigneeType: IncidentAnalyticsFilterResponseAssigneeType.Squad,
        value: el.id,
      })),
      users: currentAssignee.users.filter(el => el.value !== currentAssignee.myUsers[0].value),
      squads: currentAssignee.squads.filter(
        el => ![...currentAssignee.mySquads].map(sq => sq.value).includes(el.value),
      ),
    });
  } else if (value?.type === 'user') {
    setValue(fieldName, {
      ...currentAssignee,
      users: uniqueArrayObjects(currentAssignee.users.length ? currentAssignee?.users : [], value),
    });
  } else if (value?.type === 'squad') {
    setValue(fieldName, {
      ...currentAssignee,
      squads: uniqueArrayObjects(
        currentAssignee.squads?.length ? currentAssignee.squads : [],
        value,
      ),
    });
  }
};

const handleAssigneeRemove = (
  type: string,
  id: string,
  currentAssignee: Assignee,
  setValue: (name: keyof OnCallUsersAnalyticsFilterForm, value: any) => void,
  fieldName: 'schedule' | 'scheduleOwner',
) => {
  if (id === 'meAndMySquads') {
    setValue(fieldName, {
      ...currentAssignee,
      meAndMySquads: false,
      myUsers: [],
      mySquads: [],
    });
  } else if (type === 'user') {
    setValue(fieldName, {
      ...currentAssignee,
      users: currentAssignee.users.filter((val: AssigneeDropdownOptions) => val.value != id),
    });
  } else if (type === 'squad') {
    setValue(fieldName, {
      ...currentAssignee,
      squads: currentAssignee?.squads?.filter((val: AssigneeDropdownOptions) => val.value != id),
    });
  }
};

const FilterForm: FC<FilterFormProps> = ({ schedule, scheduleOwnerMap, meAndMySquads }) => {
  const { setValue, watch } = useFormContext<OnCallUsersAnalyticsFilterForm>();
  const selectedSchedule = watch('schedule') || [];
  const selectedScheduleOwner = watch('scheduleOwner') || getDefaultAssignee();
  const selectedOwner = useMemo(
    () => [
      ...selectedScheduleOwner.users,
      ...selectedScheduleOwner.squads,
      ...selectedScheduleOwner.myUsers,
      ...selectedScheduleOwner.mySquads,
    ],
    [selectedScheduleOwner],
  );

  const availableScheduleOptions = useMemo(() => {
    // If no owner is selected, show all unselected schedules
    if (selectedOwner.length === 0) {
      return schedule.filter(
        sch => !selectedSchedule.some(selectedSch => selectedSch.value === sch.value),
      );
    }

    // Get all schedule IDs from all selected owners
    const allAllowedScheduleIds = selectedOwner.reduce((acc: string[], owner) => {
      if (owner.value && scheduleOwnerMap[owner.value]) {
        // Convert all IDs to strings for consistent comparison
        const scheduleIds = scheduleOwnerMap[owner.value].map(id => String(id));
        acc.push(...scheduleIds);
      }
      return acc;
    }, []);

    // Filter schedules to only include those belonging to any selected owner and not already selected
    return schedule.filter(sch => {
      const scheduleValueStr = String(sch.value);
      return (
        allAllowedScheduleIds.includes(scheduleValueStr) &&
        !selectedSchedule.some(selectedSch => selectedSch.value === sch.value)
      );
    });
  }, [selectedSchedule, schedule, selectedOwner, scheduleOwnerMap]);

  return (
    <VStack>
      <Box width={'100%'} data-testid="filter-form">
        <SquadFilterDropdown
          label="Schedule Owner"
          description="Select schedule owner(s) to narrow down the list of schedules"
          value={selectedScheduleOwner}
          onClear={() => {
            setValue('scheduleOwner', getDefaultAssignee());
          }}
          type="assignee"
          placeholder="Select"
          onChange={(value: any) => {
            setValue('schedule', []);
            setValue('scheduleOwner', value);
            handleAssigneeChange(
              value,
              selectedScheduleOwner,
              setValue,
              'scheduleOwner',
              meAndMySquads,
            );
          }}
          meAndMySqaudField={selectedScheduleOwner}
          setterFunction={setValue}
          onRemove={(type: string, id: string) => {
            handleAssigneeRemove(type, id, selectedScheduleOwner, setValue, 'scheduleOwner');
          }}
        />
        <FormControlWrapper
          label="Schedule"
          value={selectedSchedule}
          onClear={() => setValue('schedule', [])}
          description="Select Schedule"
        >
          <FormSelect
            options={availableScheduleOptions}
            value={[]}
            label="Schedule"
            hideLabel
            onChange={(fieldName, value) => {
              if (Array.isArray(value)) {
                setValue('schedule', [...selectedSchedule, ...value]);
              }
            }}
            onBlur={() => {}}
            error={null}
            isMulti={true}
            fieldName="schedule"
            isClearable={false}
            data-testid="schedule-select"
          />
          <Spacer mb={2} />
          <TagStack
            tags={selectedSchedule.map(schedule => ({ label: schedule.label }))}
            variant={TAG_VARIANTS.PLAIN}
            onClose={index => {
              const newServices = [...selectedSchedule];
              newServices.splice(index, 1);
              setValue('schedule', newServices);
            }}
          />
        </FormControlWrapper>
      </Box>
    </VStack>
  );
};

export default FilterForm;
